<!-- eslint-disable max-len -->
<template>
  <v-app>
    <div class="text-center">
      <v-dialog
        v-model="showBenefitsTutorial"
        :width="$vuetify.breakpoint.xs ? '80vw' : '550px'"
        persistent
      >
        <v-card light>
          <v-card-title>Accessing Benefits</v-card-title>
          <v-card-text>
            Please take a moment to view a quick tutorial about how to access the benefits questionnaire. Click the <b><i>Next</i></b> button below to begin. Alternatively, you can press the <b><i>Skip</i></b> button to skip the tutorial.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="hideBenefitsTutorial"
            >Skip</v-btn>
            <v-btn
              color="primary"
              @click="startBenefitsTutorialStep2"
            >Next</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showBenefitsTutorialStep2"
        :width="$vuetify.breakpoint.xs ? '80vw' : '550px'"
        persistent
      >
        <v-card light>
          <v-card-title>Accessing Benefits</v-card-title>
          <v-card-text>
            Accessing the benefits questionnaire can be accomplished by clicking the <b><i>Benefits</i></b> button at the top-right of the screen.<p/><p/>Click <b><i>Next</i></b> to continue.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="startBenefitsTutorialStep3 "
            >Next</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showBenefitsTutorialStep3"
        :width="$vuetify.breakpoint.xs ? '80vw' : '550px'"
        persistent
      >
        <v-card light>
          <v-card-title>Accessing Benefits</v-card-title>
          <v-card-text>
            Now that the Benefits questionnaire has loaded you can easily switch back to the <b><i>Registration</i></b> page by clicking the <b><i>Registration</i></b> button at the top-right.<p/><p/>Click <b><i>Next</i></b> to continue.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="startBenefitsTutorialStep4"
            >Next</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showBenefitsTutorialStep4"
        :width="$vuetify.breakpoint.xs ? '80vw' : '550px'"
        persistent
      >
        <v-card light>
          <v-card-title>Accessing Benefits</v-card-title>
          <v-card-text>
            Switching between the <b><i>Benefits</i></b> questionnaire and <b><i>Registration</i></b> is easy.<p/><p/>If you'd like to turn off future hints click <b><i>Close and turn off hints</i></b>. If you'd like to leave hints turned on click <b><i>Close</i></b>.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="finishBenefitsTutorial"
            >Close and turn off hints</v-btn>
            <v-btn
              color="primary"
              @click="closeBenefitsTutorial"
            >Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="companyChooserDialog"
        width="400"
      >
        <v-card light>
          <v-card-title>Choose your company</v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item-group>
                <template v-for="(company, index) in userCompanies">
                  <!-- eslint-disable-next-line vue/valid-v-for -->
                  <v-hover v-slot="{ hover }">
                    <v-list-item
                      :key="index"
                      @click="selectCompany(company.packetGuid)"
                    >
                      <v-list-item-content>
                        <v-list-item-title style="white-space: normal;">{{ company.name }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-icon
                          v-show="hover"
                        >
                          mdi-briefcase-check
                        </v-icon>
                        <v-icon
                          v-show="!hover"
                        >
                          mdi-briefcase-outline
                        </v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </v-hover>
                </template>
              </v-list-item-group>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="supportDialog"
        :width="$vuetify.breakpoint.xs ? '80vw' : '550px'"
      >
        <v-card>
          <v-card-title class="text-h5">
            Contact Support
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-sheet
                  rounded
                  class="pa-2"
                  height="100%"
                  color="transparent"
                >
                  If you need technical support please contact Sales&nbsp;Operations at <a href="mailto:businessops@landrumhr.com" style="white-space: nowrap;">businessops@landrumhr.com</a>
                </v-sheet>
              </v-col>
              <v-col cols="12" sm="6">
                <v-sheet
                  rounded
                  height="100%"
                  class="pa-2"
                  color="transparent"
                >
                  If you encounter any issues please reach out to support. You can also try to clear your cache and then logout and login.
                </v-sheet>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              @click="supportDialog = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-app-bar
      app
      clipped-left
      flat
      outlined
      v-if="$route.name === 'Admin' || $route.name === 'Register' || $route.name === 'Invite' || $route.name === 'Status' || $route.name === 'AuditLogs' || $route.name === 'Benefits'"
    >
      <v-container class="py-0 fill-height">
        <a href="/">
          <img
            alt=""
            :src="chooseLogo"
            height="45"
          >
          <span class="sr-only">Go back to main page</span>
        </a>
      </v-container>
      <v-spacer></v-spacer>
      <div :class="helpOverlay ? 'circle' : ''">
        <v-btn
          id="btnBenefits"
          text
          @click.stop="switchViews"
          v-if="hasBenefitPacket"
          :disabled="!readyTimer"
        >{{ $route.name === "Benefits" ? "Registration" : "Benefits"}}</v-btn>
      </div>
      <v-btn
        text
        @click.stop="supportDialog = true"
      >Support</v-btn>
      <v-divider
        vertical
        class="mx-4"
      ></v-divider>
      <v-menu
        bottom
        rounded
        offset-y
        min-width="200px"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            v-on="on"
            class="pr-3 mr-5"
            tile
            width="68px"
          >
            <v-avatar>
              <v-icon large>
                mdi-account-circle
              </v-icon>
            </v-avatar>
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-list-item-content class="justify-center">
            <div class="mx-auto text-center">
              <v-avatar >
                <v-icon large>
                  mdi-account-circle
                </v-icon>
              </v-avatar>
              <h3 class="pt-1">{{ userFullName }}</h3>
              <p class="text-caption mt-1">
                {{ userEmail }}
              </p>
              <v-divider class="my-3"></v-divider>
              <v-btn
                depressed
                rounded
                text
                @click="logout"
              >
                Logout
              </v-btn>
              <div class="d-flex justify-center">
                <v-switch
                  v-model="toggleTheme"
                  inset
                  label="Dark Mode"
                >
                </v-switch>
              </div>
              <div class="d-flex justify-center">
                <v-switch
                  v-model="toggleHints"
                  inset
                  label="Show Hints"
                >
                </v-switch>
              </div>
            </div>
          </v-list-item-content>
        </v-card>
      </v-menu>
    </v-app-bar>

    <v-navigation-drawer
        app
        clipped
        permanent
        v-if="$route.name === 'Admin' || $route.name === 'Invite' || $route.name === 'Status' || $route.name === 'AuditLogs'"
    >
      <v-list>
        <v-list-item link @click="$router.push({ name: 'Admin' });">
          <v-list-item-icon>
            <v-icon>mdi-home</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Dashboard</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link @click="$router.push({ name: 'Invite' });">
          <v-list-item-icon>
            <v-icon>mdi-office-building-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Prospect Invite</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link @click="$router.push({ name: 'Status' });">
          <v-list-item-icon>
            <v-icon>mdi-list-status</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Prospect Status</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link @click="openEmailLookupInNewWindow">
          <v-list-item-icon>
            <v-icon>mdi-email-search</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Email Status</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link @click="$router.push({ name: 'AuditLogs' });">
          <v-list-item-icon>
            <v-icon>mdi-text-box-search</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Audit Logs</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

      </v-list>

    </v-navigation-drawer>

     <router-view></router-view>
  </v-app>
</template>

<script>
import { useTimeout } from '@vueuse/core';

const { ready, start } = useTimeout(3000, { controls: true });

export default {
  name: 'App',

  data: () => ({
    supportDialog: false,
    transitionStyle: null,
    helpOverlay: false,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    showBenefitsTutorialStep2: false,
    showBenefitsTutorialStep3: false,
    showBenefitsTutorialStep4: false,
    ready: null,
    start: null,
  }),
  created() {
    const darkMode = localStorage.getItem('darkMode');
    const showHints = localStorage.getItem('showHints');

    if (darkMode !== '' && typeof (darkMode) === 'string') {
      this.$vuetify.theme.dark = (darkMode === 'true');
    } else if (this.$route.name !== 'login') {
      this.$vuetify.theme.dark = true;
    }

    if (showHints !== '' && typeof (showHints) === 'string') {
      this.toggleHints = (showHints === 'true');
    }
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    });
  },
  computed: {
    readyTimer() {
      return ready.value;
    },
    btnBenefitsWidth() {
      let width = '106px';

      if (document.getElementById('btnBenefits') !== null) {
        width = `${document.getElementById('btnBenefits').getBoundingClientRect().width}px`;
      }

      return width;
    },
    boxShadow() {
      const size = this.windowHeight > this.windowWidth ? this.windowHeight : this.windowWidth;
      return `0 0 0 ${size}px rgba(0, 0, 0, 0.5)`;
    },
    toggleHints: {
      get() {
        return this.$store.state.showHints;
      },
      set(value) {
        this.$store.commit('updateShowHints', value);
      },
    },
    showBenefitsTutorial: {
      get() {
        return this.$store.state.showBenefitsTutorial;
      },
      set(value) {
        this.$store.commit('updateShowBenefitsTutorial', value);
      },
    },
    toggleTheme: {
      get() {
        return this.$vuetify.theme.dark;
      },
      set(value) {
        this.$vuetify.theme.dark = value;
        localStorage.setItem('darkMode', value);
        if (this.$vuetify.theme.dark) {
          // eslint-disable-next-line no-underscore-dangle
          window._paq.push(['trackEvent', 'Theme', 'DarkMode', 'Turned on dark mode']);
        } else {
          // eslint-disable-next-line no-underscore-dangle
          window._paq.push(['trackEvent', 'Theme', 'DarkMode', 'Turned off dark mode']);
        }
      },
    },
    chooseLogo() {
      if (this.$vuetify.theme.dark) {
        return 'https://assets.landrumhr.com/logos/Solutions-Color.png';
      }

      return 'https://assets.landrumhr.com/logos/Solutions-Color.png';
    },
    userFullName() {
      if (this.$store.state.loggedInUser.name) {
        if (!this.$store.state.loggedInUser.impersonated) {
          return this.$store.state.loggedInUser.name;
        }
      }
      if (this.$store.state.prospectPacket) {
        return this.$store.state.prospectPacket.loggedInUser.name;
      }

      if (this.$store.state.loggedInUser.name) {
        return this.$store.state.loggedInUser.name;
      }

      return 'John Doe';
    },
    userEmail() {
      if (this.$store.state.loggedInUser.email) {
        if (!this.$store.state.loggedInUser.impersonated) {
          return this.$store.state.loggedInUser.email;
        }
      }
      if (this.$store.state.prospectPacket) {
        return this.$store.state.prospectPacket.loggedInUser.email;
      }

      if (this.$store.state.loggedInUser.email) {
        return this.$store.state.loggedInUser.email;
      }

      return 'john.doe@email.com';
    },
    companyChooserDialog: {
      get() {
        return this.$store.state.companyChooserDialog;
      },
      set(value) {
        this.$store.commit('updateCompanyChooserDialog', value);
      },
    },
    userCompanies() {
      return this.$store.state.userCompanies;
    },
    hasBenefitPacket() {
      if (this.$route.name === 'Benefits' || this.$route.name === 'Register') {
        if (this.$store.state.prospectPacket != null) {
          return this.$store.state.prospectPacket.flags.hasBenefitPacket;
        }
      }
      return false;
    },
  },
  methods: {
    logout() {
      switch (this.$route.name) {
        case 'Admin':
          this.$store.dispatch('logOutOfSSO');
          break;
        case 'Invite':
          this.$store.dispatch('logOutOfSSO');
          break;
        case 'Status':
          this.$store.dispatch('logOutOfSSO');
          break;
        default:
          this.$store.dispatch('logOutOfJwt');
      }
    },
    openEmailLookupInNewWindow() {
      window.open('https://lookup.landrumhr.com', '_blank').focus();
    },
    selectCompany(companyPacketGuid) {
      localStorage.setItem('company', companyPacketGuid);
      this.companyChooserDialog = false;
      this.$router.push({ name: 'Register' });
    },
    switchViews() {
      start();
      if (this.$route.name === 'Benefits') {
        this.$router.push({ name: 'Register' });
      } else {
        this.$router.push({ name: 'Benefits' });
      }
    },
    hideBenefitsTutorial() {
      this.showBenefitsTutorial = false;
    },
    setOverlay() {
      this.helpOverlay = !this.helpOverlay;
    },
    startBenefitsTutorialStep2() {
      this.hideBenefitsTutorial();
      this.showBenefitsTutorialStep2 = true;
      this.helpOverlay = true;
    },
    startBenefitsTutorialStep3() {
      this.showBenefitsTutorialStep2 = false;
      this.$router.push({ name: 'Benefits' });
      this.showBenefitsTutorialStep3 = true;
    },
    startBenefitsTutorialStep4() {
      this.showBenefitsTutorialStep3 = false;
      this.$router.push({ name: 'Register' });
      this.showBenefitsTutorialStep4 = true;
    },
    closeBenefitsTutorial() {
      this.showBenefitsTutorialStep4 = false;
      this.helpOverlay = false;
    },
    finishBenefitsTutorial() {
      this.showBenefitsTutorialStep4 = false;
      this.helpOverlay = false;
      this.toggleHints = false;
    },
  },
};
</script>

<style lang="scss">
/* Global Styles */
html {
  overflow-y: auto
}
.theme--dark.v-card {
  background-color: #1a1a28 !important;
  color: #FFFFFF;
}
.theme--dark.v-data-table {
  background-color: transparent !important;
  color: #FFFFFF;
}
.sr-only {
  display: none;
}
.circle {
  box-shadow: v-bind('boxShadow');
  justify-content: center;
  flex-direction: column;
  align-items: center;
  border-radius: 100%;
  display: flex;
  height: v-bind('btnBenefitsWidth');
}
</style>
